import { Anchor } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { OptionsTable } from '@components';
import type { OptionsTableColumn } from '@components';
import { Modal } from '@shared/ui';
import {
    sellerFinanceApi,
    type AdvertisingLedgerEntryKind,
    type PlatformAdvertisingEntryRow,
} from '../../api/seller-finance.api';
import './SellerAdvertisingHistoryModal.css';

function entryKindLabel(kind: AdvertisingLedgerEntryKind): string {
    switch (kind) {
        case 'TOPUP':
            return 'Пополнение';
        case 'SPEND':
            return 'Клики';
        case 'REFUND':
            return 'Возврат';
        case 'ADJUSTMENT':
            return 'Корректировка';
        default:
            return kind;
    }
}

function formatMoney(amount: number): string {
    return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

const columns: OptionsTableColumn<PlatformAdvertisingEntryRow>[] = [
    {
        key: 'occurredAt',
        label: 'Дата',
        render: (row) => new Date(row.occurredAt).toLocaleString('ru-RU'),
    },
    {
        key: 'kind',
        label: 'Операция',
        render: (row) => (
            <span className="adv-history__op">{entryKindLabel(row.kind)}</span>
        ),
    },
    {
        key: 'details',
        label: 'Описание',
        render: (row) =>
            row.productId != null ? (
                <Anchor
                    component={Link}
                    to={`/products/${row.productId}`}
                    className="adv-history__link"
                >
                    {row.productName ?? `Товар #${row.productId}`}
                </Anchor>
            ) : (
                row.title ?? <span className="adv-history__muted">—</span>
            ),
    },
    {
        key: 'amountRub',
        label: 'Сумма',
        style: { whiteSpace: 'nowrap' },
        render: (row) => (
            <span
                className={
                    row.amountRub >= 0
                        ? 'adv-history__amount adv-history__amount--plus'
                        : 'adv-history__amount adv-history__amount--minus'
                }
            >
                {row.amountRub >= 0 ? '+' : ''}
                {formatMoney(row.amountRub)}
            </span>
        ),
    },
];

export function SellerAdvertisingHistoryModal({
    seller,
    onClose,
}: {
    seller: { sellerId: number; shopName?: string | null } | null;
    onClose: () => void;
}) {
    const sellerId = seller?.sellerId;
    const { data = [], isLoading, isError, refetch } = useQuery({
        queryKey: ['seller-finance', 'platform-advertising', 'entries', sellerId],
        queryFn: () => sellerFinanceApi.getPlatformAdvertisingEntries(sellerId!),
        enabled: sellerId != null,
    });

    const title = seller?.shopName?.trim()
        ? `История рекламы — ${seller.shopName}`
        : 'История рекламы';

    return (
        <Modal isOpen={seller != null} onClose={onClose} title={title} maxWidth={720}>
            <div className="adv-history">
                <OptionsTable<PlatformAdvertisingEntryRow>
                    columns={columns}
                    data={data}
                    loading={isLoading}
                    error={isError ? new Error('Не удалось загрузить историю') : undefined}
                    refetch={refetch}
                    emptyMessage="Проводок пока нет"
                />
            </div>
        </Modal>
    );
}
